/**
 * RecentActivityTable Component - Phase 2C-2 (PROPERLY FIXED)
 * 
 * Displays recent prediction activity.
 * 
 * FIXED: Badge doesn't take size prop on base Badge (only on OutcomeBadge etc.)
 * FIXED: Handle 'cancelled' status
 */

import { Card, CardHeader, CardTitle, CardContent, Badge } from '../common';
import { OutcomeBadge } from '../common';

interface Activity {
  id: string;
  type: 'prediction' | 'portfolio';
  pool_name: string;
  description: string;
  status: 'pending' | 'won' | 'lost' | 'cancelled';
  amount?: number;
  created_at: string;
}

interface RecentActivityTableProps {
  activities: Activity[];
  loading?: boolean;
  className?: string;
}

export function RecentActivityTable({
  activities,
  loading = false,
  className = '',
}: RecentActivityTableProps): JSX.Element {
  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-slate-800 rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-center py-4">No recent activity</p>
        </CardContent>
      </Card>
    );
  }

  // Map cancelled to a displayable status for OutcomeBadge
  const getOutcomeBadgeStatus = (status: Activity['status']): 'pending' | 'won' | 'lost' => {
    if (status === 'cancelled') return 'pending';
    return status;
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-3 pr-4">Pool</th>
                <th className="pb-3 pr-4">Activity</th>
                <th className="pb-3 pr-4 text-right">Amount</th>
                <th className="pb-3 pr-4">Status</th>
                <th className="pb-3 text-right">Date</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((activity) => (
                <tr key={activity.id} className="border-b border-slate-800">
                  <td className="py-3 pr-4">
                    <span className="text-slate-100">{activity.pool_name}</span>
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="default">
                        {activity.type === 'prediction' ? 'Pick' : 'Portfolio'}
                      </Badge>
                      <span className="text-slate-300">{activity.description}</span>
                    </div>
                  </td>
                  <td className="py-3 pr-4 text-right font-mono text-slate-300">
                    {activity.amount?.toLocaleString() ?? '-'}
                  </td>
                  <td className="py-3 pr-4">
                    {activity.status === 'cancelled' ? (
                      <Badge variant="default">Cancelled</Badge>
                    ) : (
                      <OutcomeBadge outcome={getOutcomeBadgeStatus(activity.status)} size="sm" />
                    )}
                  </td>
                  <td className="py-3 text-right text-slate-400">
                    {new Date(activity.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
